import CallMissedOutgoingIcon from '@mui/icons-material/CallMissedOutgoing';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import Button from '@mui/material/Button';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { IMenuItem, MenuButton } from '../../../Finance2/common-grid-defs/MenuButton';
import { useDealActions2 } from '../../hooks/useDealActions';
import {
  DealAction,
  dealBoardSettingsState,
  isOpenDrawerState,
  selectedDealActionAtom,
  selectedDealIdState,
  selectedDealState,
  useShowNearestDealOrClose,
} from '../../state/DealboardUIStateJ';
import { getNextDeal, getPrevDeal } from './dealNavigationUtils';
import { useDealAuditMenuItem } from './DealAudit';

export function ActiveDealMoreActionsButton() {
  const setIsOpenDrawer = useSetAtom(isOpenDrawerState);
  const setSelectedDeaId = useSetAtom(selectedDealIdState);
  const deal = useAtomValue(selectedDealState);
  const dealBoardSettings = useAtomValue(dealBoardSettingsState);
  const { handleClose } = useDealActions2();
  const setSelectedDealAction = useSetAtom(selectedDealActionAtom);
  const showNearestDealOrClose = useShowNearestDealOrClose();
  const dealAuditMenuItem = useDealAuditMenuItem();

  const onClose = useCallback(async () => {
    if (!deal) {
      return;
    }
    let nearestDeal = getNextDeal(dealBoardSettings, deal);
    if (!nearestDeal) {
      nearestDeal = getPrevDeal(dealBoardSettings, deal);
    }
    await handleClose(deal);
    if (!nearestDeal) {
      setIsOpenDrawer(false);
    }
    setSelectedDeaId(nearestDeal?.id ?? null);
  }, [deal, dealBoardSettings, handleClose, setIsOpenDrawer, setSelectedDeaId]);

  const handleSelectOption = useCallback(
    (optionId: DealAction | 'close') => {
      if (!deal) {
        return;
      }
      switch (optionId) {
        case 'close':
          onClose();
          return;
        case 'track':
          setSelectedDealAction({
            action: 'track',
            dealId: deal.id,
            next: () => showNearestDealOrClose(deal),
          });
          return;
        case 'pass':
          setSelectedDealAction({
            action: 'pass',
            dealId: deal.id,
            next: () => showNearestDealOrClose(deal),
          });
          return;
        case 'missed':
          setSelectedDealAction({
            action: 'missed',
            dealId: deal.id,
            next: () => showNearestDealOrClose(deal),
          });
          return;
        case 'delete':
          setSelectedDealAction({
            action: 'delete',
            dealId: deal.id,
            next: () => showNearestDealOrClose(deal),
          });
          return;
        default:
          throw new Error('Unknown option');
      }
    },
    [deal, onClose, setSelectedDealAction, showNearestDealOrClose]
  );

  const menuItems: IMenuItem[] = useMemo(
    () => [
      {
        name: 'Close',
        icon: <CheckCircleOutlineIcon fontSize='small' />,
        onClick: () => handleSelectOption('close'),
      },
      {
        name: 'Track',
        icon: <HourglassEmptyIcon fontSize='small' />,
        onClick: () => handleSelectOption('track'),
      },
      {
        name: 'Missed',
        icon: <CallMissedOutgoingIcon fontSize='small' />,
        onClick: () => handleSelectOption('missed'),
      },
      {
        name: 'Delete',
        icon: <DeleteOutlineIcon fontSize='small' />,
        onClick: () => handleSelectOption('delete'),
        divider: true,
      },
      dealAuditMenuItem,
    ],
    [handleSelectOption, dealAuditMenuItem]
  );

  if (!deal) {
    return null;
  }

  return (
    <MenuButton
      menuItems={menuItems}
      menuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'right' } }}
    />
  );
}

export function PassDealButton() {
  const deal = useAtomValue(selectedDealState);

  const setSelectedDealAction = useSetAtom(selectedDealActionAtom);

  const showNearestDealOrClose = useShowNearestDealOrClose();

  const handlePass = useCallback(() => {
    if (!deal) {
      return;
    }
    setSelectedDealAction({
      action: 'pass',
      dealId: deal.id,
      next: () => showNearestDealOrClose(deal),
    });
  }, [deal, showNearestDealOrClose, setSelectedDealAction]);

  if (!deal) {
    return null;
  }

  return (
    <Button
      variant='outlined'
      size='medium'
      color={'secondary'}
      startIcon={<ThumbDownAltOutlinedIcon />}
      onClick={handlePass}
    >
      Pass
    </Button>
  );
}
